import { Box } from "@chakra-ui/react";
import Image from "components/base/Image";
import Text from "components/base/Text";
import VideoPlayer from "components/sections/VideoPlayer";
import {
  MotionBox,
  MotionContainer,
  staggeredAppear,
  appearLeft,
  fadeIn,
} from "utils/motion";

/**
 * ImageVideoText
 *
 * Large image or video section with a text overlay below the media.
 * Based on "Image/Large Image + Text Copy 2" from Zeplin (https://zpl.io/Dp7gKEe).
 *
 * Props:
 *   title       {string}  – Section heading
 *   description {string}  – Body paragraph
 *   image       {string}  – Image src URL (used when `video` is not provided)
 *   video       {object}  – If provided, renders VideoPlayer instead of Image.
 *                           Shape: { stream, metadata, options }
 *                           stream   : { type: "hls", data: "<url>" }
 *                           metadata : { video_title, video_id, viewer_user_id }
 *                           options  : { autoplay, muted, controls, loop }
 */
export default function ImageVideoText({
  title,
  description,
  image,
  video,
  inverted = false,
  ...props
}) {
  const isVideo = !!(video && video.stream);

  return (
    <MotionBox width="100%" bg={inverted ? "white" : "black.900"}>
      <MotionContainer px={0} {...props}>
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={fadeIn}
          px={[0, null, "80px"]}
          pt={["0", null, "48px"]}
          position="relative"
        >
          <Box
            width="100%"
            // Maintain 1280:760 aspect ratio (≈ 16:9.5) on desktop
            height={["56.25vw", null, "min(640px, 52.78vw)"]}
            maxHeight={["none", null, "640px"]}
            overflow="hidden"
            position="relative"
          >
            {isVideo ? (
              <VideoPlayer
                key={video.metadata?.video_id}
                stream={video.stream}
                metadata={video.metadata ?? {}}
                type="inline"
                options={
                  video.options ?? {
                    autoplay: true,
                    muted: true,
                    controls: false,
                    loop: true,
                  }
                }
                fullBackground
                boxStyle={{ height: "100%" }}
                height="100%"
                width="100%"
              />
            ) : (
              <Image
                alt={title || description || "Image"}
                src={image}
                objectFit="cover"
                objectPosition="center"
                width="100%"
                height="100%"
              />
            )}
          </Box>
        </MotionBox>

        {/* ── Text area ──────────────────────────────────────────────── */}
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggeredAppear}
          px={[6, null, "80px"]}
          pt={[6, null, "32px"]}
          pb={[10, null, "80px"]}
          maxWidth={["100%", null, "720px"]}
        >
          {title && (
            <MotionBox variants={appearLeft} mb={[3, null, 4]}>
              <Text type="headline_sm" color={inverted ? "black.900" : "white"}>
                {title}
              </Text>
            </MotionBox>
          )}

          {description && (
            <MotionBox variants={appearLeft}>
              <Text type="p_sm" color={inverted ? "black.900" : "white"}>
                {description}
              </Text>
            </MotionBox>
          )}
        </MotionBox>
      </MotionContainer>
    </MotionBox>
  );
}
